import { z } from 'zod';
import { Source } from '@zaplify/prospects';

// Phone Number Schema
const PhoneNumberSchema = z.object({
    number: z.string(),
    type: z.string().optional(),
});

// LinkedIn Profile Verification Schema
const LinkedinProfileVerificationSchema = z.object({
    status: z.enum(['verified', 'profile_not_found']),
    checkedAt: z.date(),
});

// Education Schema
const ProspectDataEducationSchema = z.object({
    schoolName: z.string().optional(),
    schoolLinkedinUrl: z.string().optional(),
    schoolDomain: z.string().optional(),
    majors: z.array(z.string()).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});

// Experience Schema
const ProspectDataExperienceSchema = z.object({
    id: z.string(),
    organizationName: z.string().nullable().optional(),
    organizationLinkedinUrl: z.string().optional(),
    organizationDomain: z.string().optional(),
    organizationIndustry: z.string().optional(),
    organizationId: z.string().optional(),
    organizationLinkedinId: z.string().optional(),
    organizationSize: z.string().optional(),
    organizationImageUrl: z.string().optional(),
    organizationDescription: z.string().optional(),
    occupationTitle: z.string().nullable().optional(),
    occupationStartDate: z.string().nullable().optional(),
    occupationEndDate: z.string().nullable().optional(),
    occupationRole: z.string().optional(),
    occupationSubRole: z.string().optional(),
    occupationLevels: z.array(z.string()).optional(),
    occupationDescription: z.string().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
});

// Prospect Data Schema
const ProspectDataSchema = z.object({
    // Personal level
    firstName: z.string(),
    lastName: z.string(),
    fullName: z.string(),
    gender: z.string().optional(),
    location: z.string().optional(),
    locationCountry: z.string().optional(),
    locationRegion: z.string().optional(),
    locationCity: z.string().optional(),
    email: z.string().optional(),
    phoneNumbers: z.array(PhoneNumberSchema).optional(),
    birthYear: z.string().optional(),
    birthDate: z.string().optional(),
    interests: z.array(z.string()).optional(),
    skills: z.array(z.string()).optional(),

    // Social Media
    facebookProfileUrl: z.string().optional(),
    twitterProfileUrl: z.string().optional(),
    githubProfileUrl: z.string().optional(),
    linkedinProfileUrl: z.string().optional(),
    linkedinProfileVerification: LinkedinProfileVerificationSchema.optional(),
    linkedinUserId: z.string().optional(),
    linkedinProfileImgUrl: z.string().optional(),
    linkedinNumberOfConnections: z.number().optional(),
    linkedinHeadline: z.string().optional(),
    linkedinDescription: z.string().optional(),

    // Occupation level
    avatarImgUrl: z.string().optional(),
    education: z.array(ProspectDataEducationSchema).optional(),
    experiences: z.array(ProspectDataExperienceSchema).optional(),
    industry: z.string().optional(),
    occupationTitle: z.string().optional(),
    occupationRole: z.string().optional(),
    occupationSubRole: z.string().optional(),
    occupationLevels: z.array(z.string()).optional(),
    occupationDuration: z.string().optional(),
    occupationStartDate: z.string().optional(),

    // Organization level
    organizationName: z.string(),
    organizationImageUrl: z.string().optional(),
    organizationDescription: z.string().optional(),
    organizationId: z.string().optional(),
    organizationLinkedinId: z.string().optional(),
    organizationIndustry: z.string().optional(),
    organizationSize: z.string().optional(),
    organizationDomain: z.string().optional(),
    organizationLinkedinUrl: z.string().optional(),

    personId: z.string().optional(),
});

// Prospect Owner Schema
const ProspectOwnerOldSchema = z.object({
    name: z.string(),
    userId: z.string().optional(),
});

// Prospect Schema
const ProspectSchema = z.object({
    id: z.string(),
    createdByUserId: z.string(),
    userOrganizationId: z.string(),
    data: ProspectDataSchema,
    sourceId: z.string().optional(),
    source: z.nativeEnum(Source),
    owner: ProspectOwnerOldSchema.optional(),
    associatedPersonIds: z.array(z.string()).optional(),
    isReviewed: z.boolean().optional(),
    isRefreshing: z.boolean().optional(),
    refreshedAt: z.date().nullable().optional(),
    createdAt: z.date().nullable().optional(),
    occupiedStatus: z.enum(['AVAILABLE', 'QUEUED_FOR_RELEASE', 'OCCUPIED']),
});

// Prospect Owner Schema
const ProspectOwnerSchema = z.object({
    userId: z.string(),
    fullName: z.string(),
});

// Prospect With Owners Schema
const ProspectWithOwnersSchema = ProspectSchema.extend({
    owners: z.array(ProspectOwnerSchema),
});

// Company Schema
const CompanySchema = z.object({
    name: z.string(),
    domain: z.string(),
    industry: z.string(),
    linkedinUrl: z.string(),
    size: z.string(),
});

// Owners Schema
const OwnersSchema = z.object({
    prospectIdsMap: z.record(z.string(), z.array(ProspectOwnerSchema)),
    personIdsMap: z.record(z.string(), z.array(ProspectOwnerSchema)),
    linkedinUrlsMap: z.record(z.string(), z.array(ProspectOwnerSchema)),
    linkedinMemberIdsMap: z.record(z.string(), z.array(ProspectOwnerSchema)),
});

export const ProspectSimplifiedSchema = z
    .object({
        id: z.string().describe('The ID of the prospect'),

        linkedinProfileUrl: z.string().optional().describe('The LinkedIn profile URL of the prospect'),
        linkedinUserId: z.string().optional().describe('The LinkedIn member ID of the prospect'),
        email: z.string().optional().describe('The email of the prospect'),

        fullName: z.string().describe('The full name of the prospect'),
        location: z.string().optional().describe('The location of the prospect'),
        currentJobTitle: z.string().optional().describe('The current job title of the prospect'),
        currentCompany: z.string().optional().describe('The current company of the prospect'),
        linkedinHeadline: z.string().optional().describe('The LinkedIn headline of the prospect'),
        experiences: z.array(z.string()).optional().describe('The experiences of the prospect'),
    })
    .describe('A summary of a prospect');

export {
    PhoneNumberSchema,
    LinkedinProfileVerificationSchema,
    ProspectDataEducationSchema,
    ProspectDataExperienceSchema,
    ProspectDataSchema,
    ProspectOwnerOldSchema,
    ProspectSchema,
    ProspectOwnerSchema,
    ProspectWithOwnersSchema,
    CompanySchema,
    OwnersSchema,
};
